/**
 * StagedMomentChip — the moment waiting above the composer, in staging's chip
 * style: a captured frame shows its thumbnail and `Frame at 0:08`, a
 * timestamp shows `Timestamp 0:08`. × removes it.
 */
import { X } from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import { COMPOSER_CHIP_CLASS, COMPOSER_CHIP_DISMISS_CLASS, COMPOSER_CHIP_TITLE_CLASS } from './chatClasses';
import { frameAltText, timestampLabel, type StagedMoment } from './moments';

const REMOVE_LABEL = 'Remove moment';
const DISMISS_ICON_PX = 12;

const CHIP_ALIGN_CLASS = 'items-center';
const CHIP_CONTENT_CLASS = 'flex min-w-0 flex-1 items-center gap-2';
/** 36×48 — a portrait thumbnail of the staged frame. */
const THUMB_CLASS = 'h-12 w-9 shrink-0 rounded-[4px] border border-border object-cover';

export interface StagedMomentChipProps {
  staged: StagedMoment;
  onClear: () => void;
  /** A send in flight already took the moment it carries, so clearing it now wouldn't unsend it. */
  clearDisabled?: boolean;
}

function chipLabel(staged: StagedMoment): string {
  if (staged.frameUrl) return frameAltText(staged.tMs);
  return timestampLabel(staged.tMs);
}

export function StagedMomentChip({ staged, onClear, clearDisabled = false }: StagedMomentChipProps) {
  return (
    <div className={cn(COMPOSER_CHIP_CLASS, CHIP_ALIGN_CLASS)} data-testid="staged-moment-chip">
      <div className={CHIP_CONTENT_CLASS}>
        {/* Decorative: the label beside it already names the frame. */}
        {staged.frameUrl ? <img src={staged.frameUrl} alt="" className={THUMB_CLASS} /> : null}
        <span className={COMPOSER_CHIP_TITLE_CLASS}>{chipLabel(staged)}</span>
      </div>
      <button type="button" onClick={onClear} disabled={clearDisabled} aria-label={REMOVE_LABEL} className={COMPOSER_CHIP_DISMISS_CLASS}>
        <X size={DISMISS_ICON_PX} />
      </button>
    </div>
  );
}
